import { Injectable } from "@angular/core";
import { InvestigacionesServiceBackend } from "@serviceBackends";
import {
    BitacoraDTO,
    CuotaDTO,
    EncuestaDTO,
    EnvioDTO,
    FunnelDTO,
    GrupoDTO,
    GrupoHeadDTO,
    InvestigacionDTO,
    MarcadorDTO,
    PaginadoDTO,
    RespuestaDiaDTO,
    RespuestaEncuesta
} from "@DTOS";
import { Observable } from "rxjs";
import { Nilable } from "@shared/utils";

@Injectable({
    providedIn: "root"
})
export class InvestigacionesService {
    constructor(private investigacionesServiceBackend: InvestigacionesServiceBackend) { }

    public async obtenerInvestigaciones(
        pagina: number, cantidad: number, activas: boolean
    ): Promise<PaginadoDTO<InvestigacionDTO>> {
        const investigaciones = await this.investigacionesServiceBackend.getInvestigaciones(
            pagina, cantidad, activas
        );
        investigaciones.resultados.forEach(investigacion => investigacion.formatear());
        return investigaciones;
    }

    public async obtenerInvestigacionesQueryNuevo(
        query: string, pagina: number, cantidad: number, activas: boolean
    ): Promise<PaginadoDTO<InvestigacionDTO>> {
        const investigaciones = await this.investigacionesServiceBackend.getInvestigacionesQuery(
            query, pagina, cantidad, activas
        );
        investigaciones.resultados.forEach(investigacion => investigacion.formatear());
        return investigaciones;
    }

    public async obtenerInvestigacion(id: number): Promise<Nilable<InvestigacionDTO>> {
        const investigacion = await this.investigacionesServiceBackend.getInvestigacion(id);
        investigacion?.formatear();
        return investigacion;
    }

    public async agregarInvestigacion(investigacion: InvestigacionDTO): Promise<InvestigacionDTO> {
        return await this.investigacionesServiceBackend.postInvestigacion(investigacion);
    }

    public async ClonarInvestigacion(investigacion: InvestigacionDTO, moverCuestionarios: boolean,
        clonarSeries: boolean, nombre: string): Promise<InvestigacionDTO> {
        return await this.investigacionesServiceBackend.
            postClonarInvestigacion(investigacion.id, moverCuestionarios, clonarSeries, nombre);
    }

    public async guardarInvestigacionNuevo(investigacionDTO: InvestigacionDTO): Promise<InvestigacionDTO> {
        return await this.investigacionesServiceBackend.patchInvestigacion(investigacionDTO);
    }

    public async obtenerGrupos(investigacion: InvestigacionDTO): Promise<GrupoDTO[]> {
        return await this.investigacionesServiceBackend.getInvestigacionGrupos(investigacion);
    }

    public async agregarGrupo(
        investigacion: InvestigacionDTO, grupo: GrupoHeadDTO, actualizarInvestigacion: boolean = true
    ): Promise<GrupoDTO> {
        const grupoDTO = await this.investigacionesServiceBackend.postInvestigacionGrupos(investigacion, grupo);

        if (actualizarInvestigacion) {
            investigacion.grupos = [...investigacion.grupos, grupoDTO];
        }

        return grupoDTO;
    }

    public async guardarGrupo(
        investigacion: InvestigacionDTO, grupoDTO: GrupoHeadDTO, actualizarInvestigacion: boolean = true
    ): Promise<GrupoDTO> {
        const grupoNuevo = await this.investigacionesServiceBackend.putInvestigacionGrupos(grupoDTO);

        if (actualizarInvestigacion) {
            investigacion.grupos = investigacion.grupos.map(grupo => {
                if (grupo.id === grupoNuevo.id) {
                    return grupoNuevo;
                }

                return grupo;
            });
        }

        return grupoNuevo;
    }

    public async eliminarGrupo(investigacion: InvestigacionDTO, grupoHeadDTO: GrupoHeadDTO): Promise<void> {
        await this.investigacionesServiceBackend.deleteInvestigacionGrupos(investigacion, grupoHeadDTO);
        investigacion.grupos = investigacion.grupos.filter(grupo => grupo.id !== grupoHeadDTO.id);
    }

    public async agregarEnvio(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<EnvioDTO> {
        const envioDTO = await this.investigacionesServiceBackend.postInvestigacionEnvio(envio);
        investigacion.envios.push(envioDTO);
        return envioDTO;
    }

    public async actualizarEnvio(investigacion: Nilable<InvestigacionDTO>, envio: EnvioDTO): Promise<EnvioDTO> {
        const envioDTONuevo = await this.investigacionesServiceBackend.putInvestigacionEnvio(envio);
        if (investigacion != null) {
            investigacion.envios = investigacion.envios.map(envioDTO =>
                envioDTO.id === envio.id ? envioDTONuevo : envioDTO);
        }
        return envioDTONuevo;
    }

    public async obtenerRespuestas(
        investigacion: InvestigacionDTO, encuesta: EncuestaDTO, cantidadPreguntas: Nilable<number> = null,
        index: Nilable<number> = null
    ): Promise<RespuestaEncuesta[]> {
        return await this.investigacionesServiceBackend.getInvestigacionRespuestas(
            investigacion, encuesta, cantidadPreguntas, index
        );
    }

    public obtenerCuotas(investigacion: InvestigacionDTO): Promise<CuotaDTO[]> {
        return this.investigacionesServiceBackend.getCuotas(investigacion);
    }

    public agregarCuota(investigacion: InvestigacionDTO, cuota: CuotaDTO): Observable<CuotaDTO> {
        return this.investigacionesServiceBackend.postCuotas(investigacion, cuota);
    }

    public actualizarCuota(investigacion: InvestigacionDTO, cuota: CuotaDTO): Observable<CuotaDTO> {
        return this.investigacionesServiceBackend.patchCuota(investigacion, cuota);
    }

    public async obtenerMarcadores(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<MarcadorDTO[]> {
        return await this.investigacionesServiceBackend.obtenerMarcadores(investigacion, envio);
    }

    public async agregarMarcador(
        investigacion: InvestigacionDTO, envio: EnvioDTO, marcador: MarcadorDTO
    ): Promise<MarcadorDTO> {
        return await this.investigacionesServiceBackend.agregarMarcador(investigacion, envio, marcador);
    }

    public obtenerSPSS(investigacion: InvestigacionDTO): Observable<Blob> {
        return this.investigacionesServiceBackend.getSPSS(investigacion);
    }

    public async obtenerFunnels(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<FunnelDTO[]> {
        return await this.investigacionesServiceBackend.getFunnels(investigacion, envio);
    }

    public agregarFunnel(investigacion: InvestigacionDTO, envio: EnvioDTO, funnel: FunnelDTO): Observable<FunnelDTO> {
        return this.investigacionesServiceBackend.postFunnel(investigacion, envio, funnel);
    }

    public borrarFunnel(investigacion: InvestigacionDTO, envio: EnvioDTO, funnel: FunnelDTO): Observable<void> {
        return this.investigacionesServiceBackend.deleteFunnel(investigacion, envio, funnel);
    }

    public async obtenerRespuestaDia(investigacion: InvestigacionDTO, envio: EnvioDTO): Promise<RespuestaDiaDTO[]> {
        return await this.investigacionesServiceBackend.respuestasPorDia(investigacion.id, envio.id);
    }

    public obtenerBitacoras(investigacion: InvestigacionDTO): Observable<BitacoraDTO[]> {
        return this.investigacionesServiceBackend.getBitacoras(investigacion.id);
    }

    public agregarBitacora(investigacion: InvestigacionDTO, bitacora: BitacoraDTO): Observable<BitacoraDTO> {
        return this.investigacionesServiceBackend.postBitacora(investigacion.id, bitacora);
    }
}
